import React, { useEffect } from 'react';
import { Permissions, useAuth } from '@kp/react-sdk';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ErrorMessage } from '../../../components/Errors';
import { useBreadcrumb } from '../../../contexts/breadcrumb-context';
import { entityBreadcrumbLabel } from '../../../utils/breadcrumb';
import * as Entities from '../../../constants/Entities';
import { useSiteListQuery } from '../../../__generated__/types';
import { SitesList } from './SitesList';
import { entityLocation } from '../../../utils/entity';
import { useFilters } from '../../../hooks/filters';
import { useHeader } from '../../../contexts/header-context';

export const SitesListContainer: React.FC = () => {
  const { t } = useTranslation(['sites', 'general']);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { page, pageSize, query, setPage, setPageSize, setQuery } =
    useFilters();
  const { setTitle } = useHeader();

  const handleAdd = () => navigate(entityLocation(Entities.SITE, `new`));

  const { loading, error, data } = useSiteListQuery({
    variables: {
      take: pageSize,
      skip: page * pageSize,
      where: {
        name: { contains: query },
      },
    },
  });

  useBreadcrumb({
    title: entityBreadcrumbLabel(Entities.SITE, t),
    location: Entities.SITE.path,
  });

  const sites = data?.sites?.items ?? [];
  const count = data?.sites?.totalCount ?? 0;

  useEffect(() => {
    setTitle({ main: t('sites:list.title') });
  }, [setTitle, t]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <SitesList
      loading={loading}
      page={page}
      pageSize={pageSize}
      count={count}
      onChangePageSize={setPageSize}
      searchValue={query}
      onSearchSubmit={setQuery}
      onChangePage={setPage}
      sites={sites}
      onAdd={hasPermission(Permissions.SitesWrite) ? handleAdd : undefined}
    />
  );
};
